import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Dropdown,
  Empty,
  Flex,
  List,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { RoutesConfig } from '../../routes/config';
import { NOTIFICATIONS_KEY } from '../../hooks/api/notificationsConstants';
import useFetchInfiniteNotifications from '../../hooks/api/useFetchInfiniteNotifications';
import useUnreadOnlyFilter from '../../hooks/useUnreadOnlyFilter';
import useFetchUnreadCount from '../../hooks/api/useFetchUnreadCount';
import useMarkAllNotificationsRead from '../../hooks/api/useMarkAllNotificationsRead';
import useNotificationOrgId from '../../hooks/api/useNotificationOrgId';
import { sanitizeNotificationBody } from '../../utils/sanitizeNotificationBody';

const { Text } = Typography;

const MAX_BADGE_COUNT = 99;
const SCROLL_THRESHOLD_PX = 20;

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgId = useNotificationOrgId();
  const { unreadCount, errorFetchingUnreadCount } = useFetchUnreadCount();
  const [isOpen, setIsOpen] = useState(false);
  const { unreadOnly, setUnreadOnly } = useUnreadOnlyFilter();
  const {
    notifications,
    fetchNextNotifications,
    hasNextNotifications,
    isFetchingNextNotifications,
  } = useFetchInfiniteNotifications(unreadOnly);
  const { markAllRead, isMarkingAllRead } = useMarkAllNotificationsRead();
  const previousCount = useRef(unreadCount);

  useEffect(() => {
    if (
      previousCount.current !== undefined &&
      unreadCount !== previousCount.current
    ) {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_KEY, orgId, 'list'],
      });
    }
    previousCount.current = unreadCount;
  }, [unreadCount, orgId, queryClient]);

  // The service answers 403 when the user has no valid actor: no bell then.
  if (errorFetchingUnreadCount) return null;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom =
      scrollHeight - scrollTop <= clientHeight + SCROLL_THRESHOLD_PX;
    if (isAtBottom && hasNextNotifications && !isFetchingNextNotifications) {
      fetchNextNotifications();
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      open={isOpen}
      onOpenChange={setIsOpen}
      popupRender={() => (
        <Panel onScroll={handleScroll}>
          <Header>
            <Flex justify="space-between" align="center">
              <Text strong>Notifications</Text>
              <Button
                type="link"
                size="small"
                disabled={!unreadCount}
                loading={isMarkingAllRead}
                onClick={() => markAllRead()}
              >
                Mark all as read
              </Button>
            </Flex>
            <FilterRow gap="small" align="center">
              <Switch
                size="small"
                checked={unreadOnly}
                onChange={setUnreadOnly}
              />
              <Text type="secondary">Only show unread</Text>
            </FilterRow>
          </Header>
          {notifications.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                unreadOnly ? 'No unread notifications' : 'Nothing yet'
              }
            />
          ) : (
            <List
              dataSource={notifications}
              renderItem={item => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(
                      `${RoutesConfig.NotificationRedirect.basePath}/${item.id}`
                    )
                  }
                >
                  <List.Item.Meta
                    title={<Text strong={!item.read}>{item.title}</Text>}
                    description={
                      <>
                        {item.body && (
                          <BodyPreview
                            dangerouslySetInnerHTML={{
                              __html: sanitizeNotificationBody(item.body),
                            }}
                          />
                        )}
                        {dayjs(item.createdAt).format('DD.MM.YYYY HH:mm')}
                      </>
                    }
                  />
                  {!item.read && <Badge status="processing" />}
                </List.Item>
              )}
            />
          )}
          {isFetchingNextNotifications && (
            <Flex justify="center">
              <Spin size="small" />
            </Flex>
          )}
        </Panel>
      )}
    >
      <Badge
        count={unreadCount ?? 0}
        overflowCount={MAX_BADGE_COUNT}
        size="small"
        style={{ cursor: 'pointer' }}
      >
        <BellButton
          type="text"
          shape="circle"
          icon={<BellOutlined />}
          $isOpen={isOpen}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;

const Panel = styled.div`
  width: 480px;
  max-height: min(600px, 80vh);
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const BodyPreview = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  ul,
  ol {
    margin: 0;
  }

  img {
    max-width: 100%;
  }
`;

const Header = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FilterRow = styled(Flex)`
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const BellButton = styled(Button)<{ $isOpen: boolean }>`
  ${({ $isOpen, theme }) =>
    $isOpen &&
    `
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
  `}
`;
